import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Briefcase, Clock, AlertTriangle, CheckCircle, Banknote, Ruler, Camera, Users, MapPin, ArrowRight, TrendingUp, Truck, Award, Receipt } from 'lucide-react';

function StatCard({ icon: Icon, label, value, color = 'blue', sub, onClick }) {
  const colors = {
    blue: 'bg-orange-50 text-orange-600', green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600', amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600', gray: 'bg-gray-50 text-gray-600',
  };
  return (
    <div onClick={onClick} className={`bg-white rounded-xl border border-gray-200 p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon size={20} /></div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
      {sub && <p className="text-xs text-gray-400 mt-2">{sub}</p>}
    </div>
  );
}

function ProjectRow({ project, onClick }) {
  const isOverdue = project.days_overdue > 0;
  return (
    <div onClick={onClick} className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-lg cursor-pointer">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800 truncate">{project.name}</p>
        <p className="text-xs text-gray-400">{project.supervisor || 'Unassigned'} &middot; {project.location}</p>
      </div>
      <div className="text-right ml-3 shrink-0">
        {isOverdue ? (
          <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">{project.days_overdue}d overdue</span>
        ) : (
          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">{project.remaining_days}d left</span>
        )}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/admin').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;
  if (!data) return <p className="text-center text-gray-500 py-10">Failed to load dashboard</p>;

  const ps = data.projectSummary;
  const pl = data.pipeline;
  const hr = data.hr;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Check-in pending alert */}
      {hr.notCheckedIn?.length > 0 && (
        <div className="heavy-wash rounded-xl p-4 flex items-center justify-between text-white shadow-lg">
          <div>
            <p className="font-black text-white uppercase tracking-wide text-sm">Attendance Pending ({hr.notCheckedIn.length})</p>
            <p className="text-sm text-orange-100">{hr.notCheckedIn.map(u => u.name).join(', ')}</p>
          </div>
          <button onClick={() => navigate('/attendance')} className="px-4 py-2 bg-white text-orange-700 rounded-lg text-sm font-bold uppercase hover:bg-orange-50">View</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={Briefcase} label="Active Projects" value={ps.active} color="blue" onClick={() => navigate('/projects')} />
        <StatCard icon={CheckCircle} label="On Time" value={ps.onTime} color="green" onClick={() => navigate('/projects?status=active')} />
        <StatCard icon={AlertTriangle} label="Overdue" value={ps.overdue} color="red" onClick={() => navigate('/projects?status=delayed')} />
        <StatCard icon={Ruler} label="Meas. Pending" value={pl.measurementPending} color="amber" onClick={() => navigate('/projects?status=active')} />
        <StatCard icon={Banknote} label="Pay. Pending" value={pl.paymentPending} color="purple" onClick={() => navigate('/projects?status=active')} />
        <StatCard icon={Camera} label="Photoshoot" value={data.photoshootPending?.length || 0} color="gray" onClick={() => navigate('/projects?status=active')} />
      </div>

      {/* Project Progress Overview */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm text-gray-800">Project Progress</h3>
          <button onClick={() => navigate('/projects')} className="text-xs text-brand-600 hover:underline">View all</button>
        </div>
        <div className="p-4 space-y-3">
          {[...(ps.onTimeProjects || []), ...(ps.delayedProjects || [])].slice(0, 8).map(p => {
            const progress = p.progress_percentage || 0;
            const days = p.remaining_days || 0;
            return (
              <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-sm font-medium truncate">{p.name}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">{p.supervisor || ''}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-brand-600">{progress}%</span>
                    {p.days_overdue > 0 ? (
                      <span className="text-[10px] font-medium text-red-600">{p.days_overdue}d over</span>
                    ) : (
                      <span className="text-[10px] font-medium text-green-600">{days}d left</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${progress >= 75 ? 'bg-green-500' : progress >= 50 ? 'bg-brand-600' : progress >= 25 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })}
          {(ps.onTimeProjects || []).length === 0 && (ps.delayedProjects || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No active projects</p>
          )}
        </div>
      </div>

      {/* God's Eye View - Combined Map */}
      <GodsEyeView />

      {/* Financial Strip */}
      {data.financial && (
        <div className="flex gap-4 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex-1">
            <p className="text-xs text-gray-500">Total Payments Received</p>
            <p className="text-lg font-bold text-green-600">₹{Number(data.financial.totalPaymentsReceived).toLocaleString('en-IN')}</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1">
            <p className="text-xs text-gray-500">Total Site Expenses</p>
            <p className="text-lg font-bold text-red-600">₹{Number(data.financial.totalSiteExpenses).toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* On-Time Projects */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">On-Time Projects ({ps.onTimeProjects?.length || 0})</h3>
            <button onClick={() => navigate('/projects')} className="text-xs text-brand-600 hover:underline flex items-center gap-1">View all <ArrowRight size={12} /></button>
          </div>
          <div className="p-2 max-h-60 overflow-y-auto">
            {ps.onTimeProjects?.length > 0 ? ps.onTimeProjects.map(p => (
              <ProjectRow key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
            )) : <p className="text-sm text-gray-400 text-center py-4">No on-time projects</p>}
          </div>
        </div>

        {/* Delayed Projects */}
        <div className="bg-white rounded-xl border border-red-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-red-100">
            <h3 className="font-semibold text-sm text-red-700">Delayed Projects ({ps.delayedProjects?.length || 0})</h3>
          </div>
          <div className="p-2 max-h-60 overflow-y-auto">
            {ps.delayedProjects?.length > 0 ? ps.delayedProjects.map(p => (
              <ProjectRow key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
            )) : <p className="text-sm text-gray-400 text-center py-4">No delayed projects</p>}
          </div>
        </div>

        {/* HR - Attendance */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">Today's Attendance</h3>
          </div>
          <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
            {hr.todayAttendance?.map(a => (
              <div key={a.id} className="flex items-center gap-3 py-1.5">
                <div className="w-7 h-7 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">{a.user?.name?.[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.user?.name}</p>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <MapPin size={10} />{a.morning_status?.replace('_', ' ')} &middot; {a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
                {a.check_out_at ? (
                  <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">Out</span>
                ) : (
                  <span className="text-[10px] bg-green-50 px-1.5 py-0.5 rounded text-green-600">On site</span>
                )}
              </div>
            ))}
            {hr.notCheckedIn?.length > 0 && (
              <div className="border-t pt-2 mt-2">
                <p className="text-xs text-red-500 font-medium mb-1">Not Checked In ({hr.notCheckedIn.length})</p>
                {hr.notCheckedIn.map(u => (
                  <p key={u.id} className="text-xs text-gray-500 py-0.5">&bull; {u.name} ({u.role})</p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Issues */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">Open Issues ({data.issues?.openCount || 0})</h3>
          </div>
          <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
            {data.issues?.critical?.length > 0 ? data.issues.critical.map(t => (
              <div key={t.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer" onClick={() => navigate(`/projects/${t.project?.id}`)}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-[10px] text-gray-400">{t.project?.name} &middot; {t.assignee?.name || 'Unassigned'}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${t.priority === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{t.priority}</span>
              </div>
            )) : <p className="text-sm text-gray-400 text-center py-4">No critical issues</p>}
          </div>
        </div>
      </div>

      {/* Daily Update Feed */}
      {data.dailyUpdateFeed?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">Today's Updates</h3>
          </div>
          <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
            {data.dailyUpdateFeed.map(u => (
              <div key={u.id} className="flex gap-3 py-1.5">
                <div className="w-7 h-7 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">{u.author?.name?.[0]}</div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500"><span className="font-medium text-gray-700">{u.author?.name}</span> &middot; {u.project?.name}</p>
                  <p className="text-sm text-gray-800 mt-0.5">{u.content_text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transport + Incentive Quick View */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">Pending Transport</h3>
            <button onClick={() => navigate('/transport')} className="text-xs text-brand-600 hover:underline">View all</button>
          </div>
          <TransportWidget />
        </div>
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">Incentives Summary</h3>
            <button onClick={() => navigate('/incentives')} className="text-xs text-brand-600 hover:underline">View all</button>
          </div>
          <IncentiveWidget />
        </div>
      </div>
    </div>
  );
}

function SupervisorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAccounts = authUser?.role === 'accounts';
  const [expData, setExpData] = useState(null);

  useEffect(() => {
    api.get('/expense-requests').then(r => setExpData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/dashboard/supervisor').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;
  if (!data) return <p className="text-center text-gray-500 py-10">Failed to load dashboard</p>;

  const mp = data.myProjects || {};
  const att = data.myAttendance || {};

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>

      {/* Check-in Banner */}
      {!att?.checked_in && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="font-medium text-amber-800">You haven't checked in today</p>
            <p className="text-sm text-amber-600">Please check in with your photo and GPS location</p>
          </div>
          <button onClick={() => navigate('/attendance')} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium">Check In</button>
        </div>
      )}
      {att?.checked_in && !att?.checked_out && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
          <p className="text-sm text-green-700">Checked in at {att.check_in_at ? new Date(att.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'} &middot; {att.morning_status?.replace('_', ' ') || ''}</p>
          <button onClick={() => navigate('/attendance')} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">Check Out</button>
        </div>
      )}

      {/* Expense Summary for Accounts */}
      {expData && (
        <div onClick={() => navigate('/expenses')} className="bg-white rounded-xl border border-amber-200 p-4 cursor-pointer hover:shadow-md transition">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2"><Receipt size={16} className="text-amber-600" /> Expense Requests</h3>
            <span className="text-xs text-brand-600 hover:underline">View all →</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 bg-amber-50 rounded-lg"><p className="text-lg font-bold text-amber-600">{(expData.counts?.pending_admin || 0) + (expData.counts?.pending_md || 0)}</p><p className="text-[10px] text-gray-500">Pending</p></div>
            <div className="text-center p-2 bg-green-50 rounded-lg"><p className="text-lg font-bold text-green-600">{expData.counts?.unpaid || 0}</p><p className="text-[10px] text-gray-500">Unpaid</p></div>
            <div className="text-center p-2 bg-blue-50 rounded-lg"><p className="text-lg font-bold text-blue-600">{expData.counts?.paid || 0}</p><p className="text-[10px] text-gray-500">Paid</p></div>
          </div>
          {(expData.counts?.pending_admin || 0) + (expData.counts?.pending_md || 0) + (expData.counts?.unpaid || 0) > 0 && (
            <p className="text-xs text-amber-600 mt-2 font-medium">⚠ {(expData.counts?.pending_admin || 0) + (expData.counts?.pending_md || 0) + (expData.counts?.unpaid || 0)} expenses need attention</p>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Briefcase} label="My Projects" value={mp.total || 0} color="blue" onClick={() => navigate('/projects')} />
        <StatCard icon={CheckCircle} label="On Time" value={mp.onTime || 0} color="green" />
        <StatCard icon={AlertTriangle} label="Overdue" value={mp.overdue || 0} color="red" />
        <StatCard icon={TrendingUp} label="Pending Tasks" value={data.myTasks?.pending || 0} color="amber" />
      </div>

      {/* My Projects List */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold text-sm text-gray-800">My Projects</h3>
        </div>
        <div className="p-2">
          {mp.projects?.map(p => (
            <ProjectRow key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
          {mp.projects?.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No projects assigned</p>}
        </div>
      </div>

      {/* My Tasks */}
      {data.myTasks?.tasks?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-sm text-gray-800">My Tasks ({data.myTasks.pending})</h3>
          </div>
          <div className="p-3 space-y-2">
            {data.myTasks.tasks.map(t => (
              <div key={t.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-[10px] text-gray-400">{t.project?.name} &middot; Due: {t.due_date || 'No date'}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  t.priority === 'critical' ? 'bg-red-100 text-red-700' : t.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                }`}>{t.priority}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TransportWidget() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/transport?status=dispatched').then(r => setItems(r.data.receipts?.slice(0, 5) || [])).catch(() => {}); }, []);
  return (
    <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
      {items.map(t => (
        <div key={t.id} className="flex items-center justify-between py-1.5">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate flex items-center gap-1"><Truck size={12} className="text-gray-400" />{t.purchaseOrder?.po_number}</p>
            <p className="text-[10px] text-gray-400">{t.purchaseOrder?.material_description} · LR: {t.lr_number || 'N/A'}</p>
          </div>
          <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded capitalize shrink-0 ml-2">{t.status?.replace('_', ' ')}</span>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No pending transport</p>}
    </div>
  );
}

function IncentiveWidget() {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get('/incentives').then(r => setSummary(r.data.summary)).catch(() => {}); }, []);
  if (!summary) return <p className="text-sm text-gray-400 text-center p-4">Loading...</p>;
  return (
    <div className="p-4 grid grid-cols-3 gap-3 text-center">
      <div><p className="text-lg font-bold text-amber-600">{summary.total_eligible || 0}</p><p className="text-[10px] text-gray-500">Eligible</p></div>
      <div><p className="text-lg font-bold text-green-600">{summary.total_awarded || 0}</p><p className="text-[10px] text-gray-500">Awarded</p></div>
      <div><p className="text-lg font-bold text-blue-600">₹{Number(summary.total_amount || 0).toLocaleString('en-IN')}</p><p className="text-[10px] text-gray-500">Total</p></div>
    </div>
  );
}


/* ═══ GOD'S EYE VIEW — Combined Staff + Sites Map ═══ */
function GodsEyeView() {
  const [staffLocations, setStaffLocations] = useState([]);
  const [siteProjects, setSiteProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all'); // all, staff, sites
  const [selectedSite, setSelectedSite] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = () => {
      Promise.all([
        api.get('/attendance/live-locations').catch(() => ({ data: { locations: [] } })),
        api.get('/projects/map-locations').catch(() => ({ data: { projects: [] } })),
      ]).then(([staffRes, siteRes]) => {
        setStaffLocations(staffRes.data.locations || []);
        setSiteProjects(siteRes.data.projects || []);
        setLoading(false);
      });
    };
    fetchAll();
    const i = setInterval(fetchAll, 30000);
    return () => clearInterval(i);
  }, []);

  // Navigate from iframe message
  useEffect(() => {
    const handler = (e) => { if (e.data?.type === 'navigate' && e.data?.id) navigate(`/projects/${e.data.id}`); };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate]);

  if (loading) return null;

  const sitesWithGps = siteProjects.filter(p => p.site_lat && p.site_lng);
  const sitesNoGps = siteProjects.filter(p => !p.site_lat || !p.site_lng);
  const allPoints = [
    ...staffLocations.map(l => ({ lat: l.lat, lng: l.lng })),
    ...sitesWithGps.map(p => ({ lat: parseFloat(p.site_lat), lng: parseFloat(p.site_lng) })),
  ];

  const avgLat = allPoints.length > 0 ? allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length : 13.08;
  const avgLng = allPoints.length > 0 ? allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length : 80.27;

  // Build markers
  const showStaff = view === 'all' || view === 'staff';
  const showSites = view === 'all' || view === 'sites';
  const showLabour = view === 'all' || view === 'labour';

  let markersJS = '';
  if (showStaff) {
    markersJS += staffLocations.map(l =>
      `L.marker([${l.lat},${l.lng}],{icon:L.divIcon({className:'',html:'<div style="background:#22c55e;width:28px;height:28px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700">${(l.user?.name?.[0]||'?')}</div>',iconSize:[28,28],iconAnchor:[14,14]})}).addTo(map).bindPopup('<b style="color:#16a34a">${(l.user?.name||'').replace(/'/g,'')}</b><br>${l.user?.role||''}<br>${l.morning_status||''}<br>Since ${l.check_in_at?new Date(l.check_in_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}):""}')`
    ).join(';\n');
  }
  if (showSites || showLabour) {
    if (markersJS) markersJS += ';\n';
    markersJS += sitesWithGps.map(p => {
      const progress = p.progress_percentage || 0;
      const labourTotal = p.labourTotal || 0;
      const color = showLabour && !showSites ? '#8b5cf6' : (progress >= 75 ? '#22c55e' : progress >= 50 ? '#3b82f6' : progress >= 25 ? '#f59e0b' : '#ef4444');
      const label = showLabour && !showSites ? (labourTotal > 0 ? `${labourTotal}👷` : '0') : `${progress}%`;
      const days = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
      const supervisor = p.assignedUsers?.find(u => u.ProjectAssignment?.is_primary)?.name || 'Unassigned';
      const labourList = (p.labourToday || []).map(l => `${l.contractor}: ${l.count} ${l.trade||''}`).join('<br>');
      return `L.marker([${p.site_lat},${p.site_lng}],{icon:L.divIcon({className:'',html:'<div style="background:${color};width:32px;height:32px;border-radius:6px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700">${label}</div>',iconSize:[32,32],iconAnchor:[16,16]})}).addTo(map).bindPopup('<div style="min-width:160px"><b>${(p.name||'').replace(/'/g,'')}</b><br><span style="color:#666">${(p.location||'').replace(/'/g,'')}</span><br>Progress: <b>${progress}%</b> · Labour: <b>${labourTotal}</b><br>${days>=0?days+"d left":"<span style=color:red>"+Math.abs(days)+"d over</span>"}<br>Supervisor: ${supervisor.replace(/'/g,'')}<br>${labourList}</div>')`;
    }).join(';\n');
  }

  const boundsJS = allPoints.map(p => `[${p.lat},${p.lng}]`).join(',');
  const mapHtml = `<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>body{margin:0}#map{width:100%;height:100vh}</style></head><body><div id="map"></div><script>var map=L.map('map').setView([${avgLat},${avgLng}],12);L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',{attribution:'CartoDB'}).addTo(map);${markersJS};${boundsJS?`var b=L.latLngBounds([${boundsJS}]);if(b.isValid())map.fitBounds(b,{padding:[50,50],maxZoom:15})`:''}<\/script></body></html>`;

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      {/* Header with toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b flex-wrap gap-2">
        <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
          <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse inline-block" /> God's Eye View
        </h3>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {[{ key: 'all', label: 'All' }, { key: 'staff', label: `Staff (${staffLocations.length})` }, { key: 'sites', label: `Sites (${sitesWithGps.length})` }, { key: 'labour', label: `Labour` }].map(v => (
            <button key={v.key} onClick={() => setView(v.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${view === v.key ? 'bg-white shadow text-brand-600' : 'text-gray-500 hover:text-gray-700'}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-[10px] text-gray-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500 rounded-full inline-flex items-center justify-center text-white text-[8px]">S</span> Staff</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-500 rounded inline-flex items-center justify-center text-white text-[8px]">%</span> 0-25%</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 bg-amber-500 rounded inline-flex items-center justify-center text-white text-[8px]">%</span> 25-50%</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 bg-blue-500 rounded inline-flex items-center justify-center text-white text-[8px]">%</span> 50-75%</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500 rounded inline-flex items-center justify-center text-white text-[8px]">%</span> 75-100%</span>
      </div>

      {/* Map */}
      {(allPoints.length > 0) ? (
        <div className="relative h-96 bg-gray-100">
          <iframe title="Gods Eye" srcDoc={mapHtml} className="w-full h-full border-0" loading="lazy" sandbox="allow-scripts" />
        </div>
      ) : (
        <div className="h-40 flex items-center justify-center text-sm text-gray-400">No location data yet. Set GPS on projects or wait for staff check-in.</div>
      )}

      {/* Cards — Staff, Sites, Labour */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x">
        {/* Staff */}
        {showStaff && (
          <div className="p-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">Active Staff ({staffLocations.length})</p>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {staffLocations.map((loc, i) => (
                <a key={i} href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 bg-green-50 rounded-lg hover:bg-green-100 transition">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{loc.user?.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{loc.user?.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{loc.user?.role} · {loc.morning_status?.replace('_', ' ') || 'at site'} · Since {new Date(loc.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <span className="text-[10px] text-brand-600 shrink-0">↗</span>
                </a>
              ))}
              {staffLocations.length === 0 && <p className="text-xs text-gray-400 py-3 text-center">No staff checked in</p>}
            </div>
          </div>
        )}

        {/* Sites */}
        {(showSites || showLabour) && (
          <div className="p-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">{showLabour && !showSites ? 'Labour Today' : `Project Sites (${siteProjects.length})`}</p>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {siteProjects.map(p => {
                const progress = p.progress_percentage || 0;
                const days = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
                const supervisor = p.assignedUsers?.find(u => u.ProjectAssignment?.is_primary)?.name || 'Unassigned';
                const manager = p.assignedUsers?.find(u => u.ProjectAssignment?.role_on_project === 'manager')?.name || '';
                const color = progress >= 75 ? 'bg-green-500' : progress >= 50 ? 'bg-blue-500' : progress >= 25 ? 'bg-amber-500' : 'bg-red-500';
                const isExpanded = selectedSite === p.id;
                const labourTotal = p.labourTotal || 0;
                const labourList = p.labourToday || [];
                return (
                  <div key={p.id} className="bg-gray-50 rounded-lg overflow-hidden">
                    <div onClick={() => setSelectedSite(isExpanded ? null : p.id)}
                      className="flex items-center gap-2 p-2 hover:bg-blue-50 cursor-pointer transition">
                      <div className={`w-9 h-9 ${color} text-white rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0`}>{showLabour && !showSites ? `${labourTotal}👷` : `${progress}%`}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{p.location}{labourTotal > 0 ? ` · ${labourTotal} workers` : ''}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`text-[11px] font-medium ${days < 0 ? 'text-red-600' : 'text-green-600'}`}>{days < 0 ? `${Math.abs(days)}d over` : `${days}d`}</p>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t bg-white space-y-1.5">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div><span className="text-gray-400">Start:</span> <span className="font-medium">{p.start_date}</span></div>
                          <div><span className="text-gray-400">End:</span> <span className="font-medium">{p.end_date}</span></div>
                          <div><span className="text-gray-400">Supervisor:</span> <span className="font-medium">{supervisor}</span></div>
                          {manager && <div><span className="text-gray-400">Manager:</span> <span className="font-medium">{manager}</span></div>}
                          <div><span className="text-gray-400">Status:</span> <span className="font-medium capitalize">{p.status}</span></div>
                          <div><span className="text-gray-400">Progress:</span> <span className="font-medium">{progress}%</span></div>
                        </div>
                        {labourList.length > 0 && (
                          <div className="mt-1"><p className="text-[10px] font-semibold text-gray-500 mb-1">Today's Labour ({labourTotal})</p>{labourList.map((l, i) => (<p key={i} className="text-xs text-gray-600">{l.contractor} — <span className="font-medium">{l.count}</span> {l.trade || 'workers'}</p>))}</div>
                        )}
                        {p.assignedUsers?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">{p.assignedUsers.map(u => (<span key={u.id} className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-full">{u.name} ({u.ProjectAssignment?.role_on_project})</span>))}</div>
                        )}
                        <button onClick={() => navigate(`/projects/${p.id}`)} className="w-full mt-1 py-1.5 bg-brand-600 text-white rounded-lg text-xs font-medium hover:bg-brand-700">Open Project →</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { isAdmin, user } = useAuth();
  const showAdminDash = isAdmin || user?.role === 'manager';
  return showAdminDash ? <AdminDashboard /> : <SupervisorDashboard />;
}
